import * as R from 'ramda'
import { debounce } from 'throttle-debounce'
import UAParser from 'ua-parser-js'

import { changeTestObject } from '../../../src/utils/funcs.js'
import { distance } from '../../../src/utils/geodesy.js'
import { getStructureForFloorId } from '../../../src/utils/geom.js'

import { getCurrentTimestamp, initEventManager } from './events.js'

async function create (app, config) {
  const log = app.log.sublog('analytics2')

  const eventManager = await initEventManager({
    app,
    webConfigId: app.config.uuid,
    accountId: app.config.plugins.venueDataLoader.accountId,
    config,
    log
  })

  app.bus.on('analytics2/getInstallId', () => eventManager.getInstallId())

  const init = () => {
    installAppAndUserPropertiesEvent(app, eventManager, config)
    installDirectionsEvent(app, eventManager)
    installEntityViewEvent(app, eventManager)
    installExternalActionEvent(app, eventManager)
    installMapImpressionEvent(app, eventManager)
    installSearchEvent(app, eventManager)
    installUserPositionEvent(app, eventManager)
    installVenueLoadEvent(app, eventManager, config)

    app.bus.monitor('events/endSession', () => { // Does not appear to be used currently...
      eventManager.submitEvent({ _eventType: 'sessionEnd' })
    })
  }

  return {
    init
  }
}

const getCurrentUrlReferrer = async (bus) => {
  const urlParamsResponses = await bus.send('deepLinking/getStateParms')
  const urlParams = urlParamsResponses.length ? urlParamsResponses[0] : location.search
  const url = location.origin + location.pathname + '?' + urlParams
  return url
}

const poiToPos = poi => ({
  floorId: poi.position.floorId,
  lat: poi.position.latitude,
  lng: poi.position.longitude,
  isUserPosition: false,
  entityId: parseInt(poi.poiId)
})

const poiIdToPosition = async (app, poiId) => {
  const poi = await app.bus.get('poi/getById', { id: poiId })
  return poiToPos(poi)
}

const endpointToPosition = (app, ep) => {
  const isInt = s => s.search(/^-?\d+$/) >= 0 // true if string is integer "683", etc.

  if (typeof ep === 'string' && isInt(ep))
    return poiIdToPosition(app, parseInt(ep))

  if (typeof ep === 'object' && ep.lat) {
    const { lat, lng, floorId } = ep
    return {
      floorId,
      lat,
      lng,
      isUserPosition: ep.isPhysicalLocation
    }
  }
}

function installDirectionsEvent (app, eventManager) {
  // from/to will either be a poiId or a location
  app.bus.monitor('event/directions', routeInfo => sendEventDirections(app, eventManager, routeInfo))
  app.bus.monitor('navigation/show', ({ reset }) => { if (reset) directionsSent.length = 0 }) // reset directionsSent memory
}

const directionsSent = [] // track which direcitons events we have sent - see multipoint handling below
async function sendEventDirections (app, eventManager, { endpointIds, routedThroughQueues, isPrimaryRouteAccessible, referrer }) {
  const timestamp = getCurrentTimestamp()

  const positions = await Promise.all(endpointIds.map(id => endpointToPosition(app, id)))

  const initiation = referrer === 'prog' ? 'prog' : 'user'
  if (referrer === 'prog')
    referrer = null

  if (positions.length <= 2) {
    eventManager.submitEvent({
      timestamp,
      _eventType: 'directions',
      initiation,
      referrer,
      fromPosition: positions[0],
      toPosition: positions[1],
      routedThroughQueues,
      accessibleRoute: isPrimaryRouteAccessible,
      urlReferrer: await getCurrentUrlReferrer(app.bus)
    })
    directionsSent.push([positions[0].entityId, positions[1].entityId]) // note that we sent this directions event
  } else {
    eventManager.submitEvent({
      timestamp,
      _eventType: 'multipointDirections',
      initiation,
      referrer,
      waypoints: positions,
      routedThroughQueues,
      accessibleRoute: isPrimaryRouteAccessible,
      urlReferrer: await getCurrentUrlReferrer(app.bus)
    })
    // if we have not yet sent the (non-multipoint) directions event from the first position to the last, do so now
    if (!directionsSent.some(dir => positions.at(0).entityId === dir[0] && positions.at(-1).entityId === dir[1])) {
      eventManager.submitEvent({
        timestamp,
        _eventType: 'directions',
        initiation,
        referrer,
        fromPosition: positions[0],
        toPosition: positions.at(-1),
        routedThroughQueues,
        accessibleRoute: isPrimaryRouteAccessible,
        urlReferrer: await getCurrentUrlReferrer(app.bus)
      })
      directionsSent.push([positions.at(0).entityId, positions.at(-1).entityId]) // can't forget to note this for next time
    }
  }
}

function installEntityViewEvent (app, eventManager) {
  const getActionTypesFromPoi = (poi) => {
    const types = []
    if (poi.phone && poi.phone.length > 0) {
      types.push('phone')
    }
    if (poi.links && poi.links.length > 0) {
      poi.links.forEach(link => {
        if (link.type === 'primary') {
          types.push('website')
        } else {
          types.push(link.type)
        }
      })
    }
    return types
  }
  app.bus.monitor('poiDetails/showPoi', async ({ poi, info = {} }) => {
    const referrer = info.referrer || null
    const initiation = referrer !== null ? 'user' : 'prog' // if there's no referrer in the info object we assume the event was triggered programmatically
    const timestamp = getCurrentTimestamp()
    eventManager.submitEvent({
      _eventType: 'entityView',
      entityId: poi.poiId,
      actionTypes: getActionTypesFromPoi(poi),
      urlReferrer: await getCurrentUrlReferrer(app.bus),
      initiation,
      referrer,
      timestamp
    })
  })
}

function installMapImpressionEvent (app, eventManager, debounceTimeOverride) {
  const MAP_IMPRESSION_DEBOUNCE_TIME = 2000 // we only want to send the event when the users stops on a certain map view for X amount of time
  const objectChanged = changeTestObject()

  const submitMapImpression = debounce(debounceTimeOverride || MAP_IMPRESSION_DEBOUNCE_TIME, async (mapState) => {
    const timestamp = getCurrentTimestamp()
    const mapCenter = await app.bus.get('map/getMapCenter')
    const center = R.pick(['lat', 'lng', 'ordinal', 'structureId', 'floorId'], mapCenter)
    const visibleEntities = await app.bus.get('map/getVisibleEntities')
    const radius = await app.bus.get('map/getCurrentMapRadius')

    if (objectChanged({ mapCenter, visibleEntities, radius }))
      eventManager.submitEvent({
        _eventType: 'mapImpression',
        timestamp,
        center,
        zoomLevel: mapState.zoom,
        heading: 0 + mapState.bearing, // sometimes Mapbox report bearing as -0, this line is for making sure we won't send it like that
        pitch: mapState.pitch,
        visibleEntities,
        radius
      })
  })

  app.bus.monitor('map/moveEnd', submitMapImpression)
  app.bus.monitor('map/userMoving', submitMapImpression)
  app.bus.monitor('map/floorChanged', submitMapImpression)
  app.bus.monitor('map/ordinalChanged', submitMapImpression)
  app.bus.monitor('map/entitySelectionUpdated', submitMapImpression)
}

function installSearchEvent (app, eventManager) {
  const submitSearchEvent = async (referrer, searchMethod, query, entities) => {
    const timestamp = getCurrentTimestamp()

    const initiation = referrer === 'prog' ? 'prog' : 'user'
    if (referrer === 'prog') {
      referrer = null
      searchMethod = null
    }

    eventManager.submitEvent({
      _eventType: 'search',
      timestamp,
      initiation,
      referrer,
      searchMethod,
      query,
      entities,
      urlReferrer: await getCurrentUrlReferrer(app.bus)
    })
  }

  app.bus.monitor('event/search', ({ referrer, searchMethod, query, entities }) => submitSearchEvent(referrer, searchMethod, query, entities))
}

function installUserPositionEvent (app, eventManager) {
  const MIN_REPORT_METERS_DIFF = 2 // don't report events with movements less than this
  let lastUserPosition = {} // hold last update to determine distance

  app.bus.monitor('bluedot/locationUpdate', async ({ hasLocation, latitude, longitude, floorId, ordinal, userPositionSource, accuracy }) => {
    if (hasLocation) {
      if (!lastUserPosition ||
        floorId !== lastUserPosition.floorId ||
        distance(latitude, longitude, lastUserPosition.lat, lastUserPosition.lng) > MIN_REPORT_METERS_DIFF) {
        const userPosition = {
          lat: latitude,
          lng: longitude
        }
        if (ordinal !== null)
          userPosition.ordinal = ordinal
        if (floorId) {
          userPosition.floorId = floorId
          userPosition.structureId = getStructureForFloorId(await app.bus.get('venueData/getStructures'), floorId).id
        }
        eventManager.submitEvent({
          _eventType: 'userPosition',
          userPosition,
          userPositionSource,
          userPositionErrorRadius: accuracy
        })
        lastUserPosition = { latitude, longitude, floorId }
      }
    } else { // !hasLocation
      lastUserPosition = null
      eventManager.removeCachedUserPosition()
    }
  })
}

function installVenueLoadEvent (app, eventManager, config) {
  app.bus.monitor('venueData/mapDataLoaded', async () => {
    const dlp = config.deepLinkProps
    const referrerInstallId = dlp && dlp.refInstallId ? dlp.refInstallId : null
    // if we get a referrerInstallId and there's a ho URL parameter that means we are loading from QR code handoff, when no ho that means it's a shared link
    const loadReferrer = referrerInstallId !== null ? (dlp && dlp.ho !== undefined ? 'qrCode' : 'linkShare') : null
    const timestamp = getCurrentTimestamp()
    const venueData = await app.bus.get('venueData/getVenueData')
    eventManager.submitEvent({
      _eventType: 'venueLoad',
      venueId: venueData.id,
      venueVersion: venueData.version,
      venueFormat: venueData.formatVersion,
      remote: true,
      urlReferrer: await getCurrentUrlReferrer(app.bus),
      referrerInstallId,
      loadReferrer,
      timestamp
    })
  })
}

function installExternalActionEvent (app, eventManager) {
  app.bus.monitor('events/externalAction', ({ poi, actionType, actionValue }) => {
    eventManager.submitEvent({
      _eventType: 'externalAction',
      initiation: 'user',
      actionType,
      actionValue,
      referrer: 'entityView',
      entityId: poi.poiId
    })
  })
}

async function installAppAndUserPropertiesEvent (app, eventManager, config) {
  const havePropsChanged = (newProps, oldProps) => !R.whereEq(newProps, oldProps)
  const getAppAndUserProperties = () => JSON.parse(localStorage.getItem(`ll_installId_${app.config.uuid}`))
  const storeNewAppAndUserProperties = (data) => localStorage.setItem(`ll_installId_${app.config.uuid}`, JSON.stringify(data))

  const checkAndSendEvent = (newProps) => {
    const stored = getAppAndUserProperties()
    if (havePropsChanged(newProps, stored)) {
      const fullProps = { ...stored, ...newProps }
      storeNewAppAndUserProperties(fullProps)
      eventManager.submitEvent({
        _eventType: 'appAndUserProperties',
        ...fullProps
      })
    }
  }

  const shortAccountId = await app.bus.get('venueData/getAccountId')
  const parsedUserAgent = new UAParser().getResult()

  const current = {
    installId: eventManager.getInstallId(),
    shortAccountId,
    productName: config.productName,
    productVersion: app.info.wePkg.version,
    deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    deviceOSName: parsedUserAgent.os.name,
    deviceOSVersion: parsedUserAgent.os.version,
    uiLocale: app.i18n().language,
    browserName: parsedUserAgent.browser.name,
    browserVersion: parsedUserAgent.browser.version,
    webConfigId: app.config.uuid,
    hostAppId: config.hostAppId,
    hostAppVersion: config.hostAppVersion,
    hostAppProperties: config.hostAppProperties
  }

  checkAndSendEvent(current)

  app.bus.on('i18n/setUILang', ({ lang }) => {
    checkAndSendEvent({ uiLocale: lang })
  })

  app.bus.get('kioskAdmin/getKioskData')
    .then(kioskData => {
      const kioskName = R.path(['kiosk', 'name'], kioskData) || null
      const kioskLocation = R.path(['kiosk', 'location'], kioskData) || null
      if (kioskLocation) {
        kioskLocation.isUserPosition = true
      }
      checkAndSendEvent({ kioskName, kioskLocation })
    })
    .catch(() => true)
}

export {
  create,
  sendEventDirections,
  installMapImpressionEvent
}
